import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '@/components/ui/dialog';
import { toast } from 'sonner';
import {
  Users, Phone, Mail, MessageSquare, Video, Star,
  Stethoscope, Heart, UserCheck, Send
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PatientCareTeam = () => {
  const [careTeam, setCareTeam] = useState([]);
  const [showMessage, setShowMessage] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [messageText, setMessageText] = useState('');
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCareTeam();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem('token');
    return { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
  };

  const fetchCareTeam = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/phase35/care-team/my`, {
        headers: getAuthHeaders()
      });

      if (response.ok) {
        const data = await response.json();
        setCareTeam(data.care_team || []);
      }
    } catch (error) {
      console.error('Error fetching care team:', error);
    }
    setLoading(false);
  };

  const sendMessage = async () => {
    if (!selectedMember || !messageText.trim()) return;

    try {
      const response = await fetch(`${API_URL}/api/phase35/messages/send`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({
          recipient_id: selectedMember.id,
          recipient_type: 'user',
          message: messageText,
          priority: 'normal'
        })
      });

      if (response.ok) {
        toast.success(`Message sent to ${selectedMember.name}`);
        setShowMessage(false);
        setMessageText('');
        setSelectedMember(null);
      } else {
        toast.error('Failed to send message');
      }
    } catch (error) {
      toast.error('Error sending message');
    }
  };

  const getRoleIcon = (role) => {
    switch (role?.toLowerCase()) {
      case 'physician': return <Stethoscope className="h-5 w-5 text-blue-600" />;
      case 'nurse': return <Heart className="h-5 w-5 text-red-500" />;
      case 'aide': return <UserCheck className="h-5 w-5 text-green-600" />;
      default: return <Users className="h-5 w-5 text-gray-600" />;
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'physician': return 'bg-blue-100 text-blue-700';
      case 'nurse': return 'bg-red-100 text-red-700';
      case 'aide': return 'bg-green-100 text-green-700';
      case 'social_worker': return 'bg-purple-100 text-purple-700';
      case 'chaplain': return 'bg-amber-100 text-amber-700';
      default: return 'bg-gray-100 text-gray-700';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="patient-care-team">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-6 w-6 text-teal-600" />
            Your Care Team
          </CardTitle>
          <CardDescription>
            Contact your hospice care team members directly
          </CardDescription>
        </CardHeader>
        <CardContent>
          {careTeam.length === 0 ? (
            <div className="text-center py-8">
              <Users className="h-12 w-12 mx-auto text-gray-300 mb-3" />
              <p className="text-gray-500">No care team assigned yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Your care team will appear here once assigned
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {careTeam.map((member) => (
                <div
                  key={member.id}
                  className="flex items-start gap-4 p-4 border rounded-lg hover:bg-gray-50"
                >
                  <div className="w-12 h-12 bg-teal-100 rounded-full flex items-center justify-center">
                    {getRoleIcon(member.role)}
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <h4 className="font-medium">{member.name}</h4>
                      {member.is_primary && (
                        <Star className="h-4 w-4 text-amber-500 fill-amber-500" />
                      )}
                    </div>
                    <Badge className={`mt-1 ${getRoleBadgeColor(member.role)}`}>
                      {member.assignment_type || member.role}
                    </Badge>
                    
                    {member.phone && (
                      <p className="text-sm text-gray-500 mt-2 flex items-center gap-1">
                        <Phone className="h-3 w-3" /> {member.phone}
                      </p>
                    )}
                    {member.email && (
                      <p className="text-sm text-gray-500 flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {member.email}
                      </p>
                    )}
                    
                    <div className="flex gap-2 mt-3">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedMember(member);
                          setShowMessage(true);
                        }}
                        data-testid={`message-${member.id}`}
                      >
                        <MessageSquare className="h-3 w-3 mr-1" />
                        Message
                      </Button>
                      {member.phone && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => window.location.href = `tel:${member.phone}`}
                        >
                          <Phone className="h-3 w-3 mr-1" />
                          Call
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Emergency Contact Card */}
      <Card className="border-red-200 bg-red-50">
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-red-100 rounded-full">
              <Phone className="h-6 w-6 text-red-600" />
            </div>
            <div>
              <h4 className="font-medium text-red-700">24/7 Hospice Hotline</h4>
              <p className="text-red-600 text-lg font-bold">703-578-7108</p>
              <p className="text-sm text-red-500">For urgent care needs, call anytime</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Message Dialog */}
      <Dialog open={showMessage} onOpenChange={setShowMessage}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Message {selectedMember?.name}</DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Type your message here..."
              value={messageText}
              onChange={(e) => setMessageText(e.target.value)}
              rows={4}
              data-testid="message-input"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowMessage(false)}>Cancel</Button>
            <Button onClick={sendMessage} data-testid="send-message-btn">
              <Send className="h-4 w-4 mr-2" />
              Send Message
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PatientCareTeam;
